import React, {type PropsWithChildren, useEffect, useMemo} from 'react';
import type {ThesaurusId} from 'services/thesaurus/types';
import {useLocalization} from 'localization';
import {selectSettingsPreferredLocale} from 'services/settings';
import type {SetLoadStatus} from 'services/utils/hooks';
import {useBatchedLoadStatus} from 'services/utils/hooks';
import {useAppSelector, useAppStore} from 'store/hooks';
import {SceneError} from 'components/scene/SceneError';
import {SceneLoader} from 'components/scene/SceneLoader';
import {thesaurusMaxAgeSeconds, useGetThesaurusQuery} from '../thesaurusApi';
import {selectThesaurusData} from '../thesaurusSelectors';

type Props = {
  thesauruses: ThesaurusId[] | undefined;
};

const ThesaurusLoader: React.FC<{
  id: ThesaurusId;
  onStatus: SetLoadStatus;
}> = ({id, onStatus}) => {
  const language = useAppSelector(selectSettingsPreferredLocale);
  const {data, isError} = useGetThesaurusQuery(
    {
      id,
      language,
    },
    {
      refetchOnReconnect: true,
      refetchOnMountOrArgChange: thesaurusMaxAgeSeconds,
    },
  );

  useEffect(() => {
    // Reuse old data if it exist. A loaded thesaurus is never reported as failed.
    if (data) {
      onStatus(id, 'Loaded');
    } else if (isError) {
      onStatus(id, 'Failed');
    } else {
      onStatus(id, 'Loading');
    }
  }, [data, id, isError, onStatus]);

  return null;
};

/**
 * Will only render children after thesauruses are successfully loaded.
 * Shows spinner while loading, and an error message if any fails
 */
export const ThesaurusesGate: React.FC<PropsWithChildren<Props>> = ({
  thesauruses,
  children,
}) => {
  const {t} = useLocalization();
  const language = useAppSelector(selectSettingsPreferredLocale);
  const store = useAppStore();

  const idsKey = (thesauruses ?? []).join('|');
  const allIds = useMemo<ThesaurusId[]>(
    () => (idsKey === '' ? [] : (idsKey.split('|') as ThesaurusId[])),
    [idsKey],
  );

  const notLoadedIds = useMemo<ThesaurusId[]>(() => {
    const state = store.getState();
    return allIds.filter(id => !selectThesaurusData(id, language)(state));
  }, [allIds, language, store]);

  const {status, setStatus} = useBatchedLoadStatus(notLoadedIds);

  return (
    <>
      {allIds.map(id => (
        <ThesaurusLoader key={id} id={id} onStatus={setStatus} />
      ))}
      {status === 'Failed' ? (
        <SceneError error={t('thesaurus.load.failed.label')} applySceneHeight />
      ) : status === 'Loading' ? (
        <SceneLoader
          loadingType={t('general.entity.thesaurus')}
          applySceneHeight
        />
      ) : (
        children
      )}
    </>
  );
};
